import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  SceneTransition,
  SlideTimeBar,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, FORENSICS, WHY, FIX, RESULT, LESSON, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({ children, size = 92 }) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK · 31 chars in, 13k tokens, 72s */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const swell = interpolate(frame, [30, 80], [0.35, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="EAKC · chat latency" color={COLORS.amber} />
          <Big>
            A 31-char question.{" "}
            <span style={{ color: COLORS.amber, opacity: swell }}>13,000 tokens.</span>
            <br />
            <span style={{ color: COLORS.red, opacity: swell }}>72 seconds.</span>
          </Big>
          <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim, marginTop: 26 }}>
            why your chatbot gets slower the longer you talk to it
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="amber" />
    </AbsoluteFill>
  );
};

/* 02 — FORENSICS · the agent_usage_logs rows */
const LogRow: React.FC<{ q: string; tokens: string; ms: string; alert?: boolean; opacity: number }> = ({
  q,
  tokens,
  ms,
  alert,
  opacity,
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "150px 220px 160px",
      gap: 18,
      padding: "12px 18px",
      fontFamily: FONTS.mono,
      fontSize: 28,
      color: alert ? COLORS.red : COLORS.text,
      background: alert ? "rgba(255,77,77,0.08)" : "transparent",
      borderRadius: 8,
      opacity,
    }}
  >
    <span style={{ color: COLORS.dim }}>{q}</span>
    <span style={{ fontWeight: 700 }}>{tokens}</span>
    <span>{ms}</span>
  </div>
);

export const Scene02Forensics: React.FC = () => {
  const frame = useCurrentFrame();
  const r = (f: number) => interpolate(frame, [f, f + 22], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.forensics.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="read the logs, don't guess" color={COLORS.cyan} />
        <Panel accent="cyan" title="agent_usage_logs — last 48h" width={760} padding={16}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "150px 220px 160px",
              gap: 18,
              padding: "4px 18px 8px",
              fontFamily: FONTS.mono,
              fontSize: 20,
              color: COLORS.dim,
              letterSpacing: "0.06em",
            }}
          >
            <span>query</span>
            <span>tokens_in</span>
            <span>latency</span>
          </div>
          <LogRow q="31 chars" tokens="13,839" ms="72,623 ms" alert opacity={r(30)} />
          <LogRow q="31 chars" tokens="13,419" ms="68,307 ms" alert opacity={r(48)} />
          <LogRow q="53 chars" tokens="12,481" ms="52,226 ms" alert opacity={r(66)} />
          <LogRow q="31 chars" tokens="— cache hit" ms="5,814 ms" opacity={r(84)} />
        </Panel>
        <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.text, marginTop: 26, maxWidth: 1000, textAlign: "center" }}>
          a <b style={{ color: COLORS.amber }}>31-character</b> query sending{" "}
          <b style={{ color: COLORS.red }}>~14,000 input tokens</b> — that's the conversation history, not retrieval
        </div>
      </AbsoluteFill>
      <Caption lines={FORENSICS} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 03 — WHY · less context is MORE accurate */
export const Scene03Why: React.FC = () => {
  const frame = useCurrentFrame();
  const a = interpolate(frame, [20, 45], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const b = interpolate(frame, [55, 80], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c = interpolate(frame, [130, 160], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.why.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="more context = worse answers" color={COLORS.violet} />
        <div style={{ display: "flex", gap: 26, alignItems: "stretch" }}>
          <div style={{ opacity: a }}>
            <Panel accent="violet" title="Lost in the Middle" width={420}>
              <div style={{ fontFamily: FONTS.body, fontSize: 26, lineHeight: 1.55, color: COLORS.text }}>
                models lose what's buried in the middle of a long prompt — a 20-point accuracy swing by position
              </div>
            </Panel>
          </div>
          <div style={{ opacity: b }}>
            <Panel accent="magenta" title="Context Rot" width={420}>
              <div style={{ fontFamily: FONTS.body, fontSize: 26, lineHeight: 1.55, color: COLORS.text }}>
                all 18 frontier models degrade as input grows — even Gemini Flash & Pro, even on trivial tasks
              </div>
            </Panel>
          </div>
        </div>
        <div style={{ opacity: c, marginTop: 30 }}>
          <Big size={56}>
            <span style={{ color: COLORS.green }}>~300 focused tokens</span> beat{" "}
            <span style={{ color: COLORS.red }}>113k full tokens</span>
          </Big>
        </div>
      </AbsoluteFill>
      <Caption lines={WHY} accent="violet" />
    </AbsoluteFill>
  );
};

/* 04 — FIX · size-budget the history + Flash */
const TRIM_CODE = `// src/lib/chat/trim-history.ts
export function trimConversationHistory(messages, {
  maxMessages = 20,       // hard count cap
  maxTotalChars = 8000,   // ~2k tokens, newest-first
  maxMessageChars = 2000, // one giant answer can't dominate
} = {}) {
  const capped = messages.map(truncateToCap);
  // walk newest -> oldest until the budget is hit
  return keepWithinBudget(capped).reverse();
}`;

export const Scene04Fix: React.FC = () => {
  const frame = useCurrentFrame();
  const flash = interpolate(frame, [300, 340], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.fix.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the fix is small" color={COLORS.green} />
        <div style={{ display: "flex", gap: 30, alignItems: "center" }}>
          <CodePanel code={TRIM_CODE} language="typescript" filename="trim-history.ts" highlightLines={[3, 4, 5]} width={860} fontSize={24} />
          <div style={{ display: "flex", flexDirection: "column", gap: 18, opacity: flash }}>
            <Callout label="cap each message" accent="green" width={300}>
              a 4,000-char recap → 2,000
            </Callout>
            <Callout label="cap the total" accent="cyan" width={300}>
              ~13k → ~3k tokens
            </Callout>
            <Callout label="model → Flash" accent="lime" width={300}>
              deep mode: gemini-2.5-pro → 2.5-flash
            </Callout>
          </div>
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 26 }}>
          keep the newest verbatim · never delete the raw transcript
        </div>
      </AbsoluteFill>
      <Caption lines={FIX} accent="green" />
    </AbsoluteFill>
  );
};

/* 05 — RESULT · 72s -> 4.3s */
export const Scene05Result: React.FC = () => {
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.result.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="same question, measured" color={COLORS.green} />
        <div style={{ display: "flex", gap: 90, alignItems: "flex-end" }}>
          <div style={center}>
            <MetricCounter value={4.3} from={72} startFrame={20} durationFrames={70} accent="green" suffix="s" decimals={1} fontSize={180} />
            <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim, marginTop: 8 }}>latency · was 72s</div>
          </div>
          <div style={center}>
            <MetricCounter value={3} from={13.8} startFrame={30} durationFrames={70} accent="cyan" prefix="~" suffix="k" decimals={1} fontSize={180} />
            <div style={{ fontFamily: FONTS.mono, fontSize: 24, color: COLORS.dim, marginTop: 8 }}>input tokens · was 13.8k</div>
          </div>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.text, marginTop: 34 }}>
          no new model · no bigger window — <b style={{ color: COLORS.green }}>less context, better placed</b>
        </div>
      </AbsoluteFill>
      <Caption lines={RESULT} accent="green" />
    </AbsoluteFill>
  );
};

/* 06 — LESSON */
export const Scene06Lesson: React.FC = () => {
  const frame = useCurrentFrame();
  const u = interpolate(frame, [40, 80], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.lesson.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the generalizable lesson" color={COLORS.cyan} />
          <Big size={76}>
            Quality on a cheap model is a{" "}
            <span style={{ color: COLORS.green }}>context problem</span>,
            <br />
            not a <span style={{ color: COLORS.red, opacity: 0.5 + u * 0.5 }}>model-size problem</span>.
          </Big>
          <div style={{ fontFamily: FONTS.body, fontSize: 30, color: COLORS.dim, marginTop: 28, maxWidth: 1200, textAlign: "center", opacity: u }}>
            a Flash model with a lean, relevant, well-ordered prompt beats a Pro model drowning in tokens
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={LESSON} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 07 — OUTRO */
export const Scene07Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.2em", color: COLORS.cyan, marginBottom: 20 }}>
          EAKC · BUILDING THE SELF-EVOLVING BRAIN
        </div>
        <Big size={70}>
          Read the logs. Trim the context.
          <br />
          <span style={{ color: COLORS.green }}>Prove the change.</span>
        </Big>
        <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 26 }}>
          the cheapest model, used well, wins
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="green" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
